<script setup lang="ts">
/**
 * 発注ステータスバッジ
 */
import { computed, defineProps, ref, watch, withDefaults } from 'vue';
import { i18n } from '@/i18n';
import { onMounted } from 'vue';
import { AlertType } from '@/coreDomainsModule/entities/hasRecommendOrders';
import { simpleCopy } from '@/utils/deepCopy';
import AlertCell from '@/corePagesModule/components/table/AlertCell.vue';
import { AlertBasicDataObject } from '@/coreDomainsModule/alert';
import { OrderAlertKinds } from '@/orderTableModule/domains/entities/Alert';
import { assertNever } from '@/coreDomainsModule/assertNever';

// 2024/12/17 現状TableCellをWrapする形で実装していますが、良いタイミングでテーブルから直接呼ぶ形に変更してください。

/**
 * propsの定義
 * @param alertTypes - アラートの種類
 * @param deliveryDate - 納期
 * @param orderAmount - 発注数量
 */
type OrderAlertProps = {
  // テーブルに直接実装する際にはInventoryManageを参考にしてAlert共通のTypeを使用するように実装してください
  alertTypes: AlertType[];
  deliveryDate: string;
  orderAmount: string;
  isOpening: boolean;
};

const props = withDefaults(defineProps<OrderAlertProps>(), {});

const emits = defineEmits<{
  (e: 'click', isOpening: boolean): void;
  (e: 'onNext', alertKind: string): void;
}>();

const t = i18n.t;

// ランダムID生成
const imgId = ref<string>('');
onMounted(() => {
  imgId.value = Math.random().toString(32).substring(2);
});

const alertTypes = ref<AlertType[]>([]);
onMounted(() => {
  alertTypes.value = props.alertTypes;
});

/**
 * アラートかどうか
 * @param alertType - アラートの種類
 */
const isAlert = (alertType: AlertType) => {
  return alertType === 'OrderAmountWarning' || alertType === 'DeliveryDateWarning';
};

/**
 * アラートラベル
 * @param alertType - アラートの種類
 */
const alertLabel = (alertType: AlertType): string => {
  switch (alertType) {
    case 'OrderAmountWarning':
      return t('在庫がゼロ以下になる予想が出ています。発注数量や納期の見直し、または追加発注を検討してください。');
    case 'DeliveryDateWarning':
      return t('予定していた納期が過ぎています。倉庫へ入荷状況を確認してください。');
    case 'DeliveryDateInfo':
      return t('納期当日になりました。倉庫へ納入状況を確認してください。');
    case 'DeliveryDateConfirmedInfo':
      return t('発注を出力してから1週間経過しました。仕入先へ数量・納期の回答を確認してください。');
    default:
      assertNever(alertType);
      return '';
  }
};

watch(
  () => props.alertTypes,
  () => {
    alertTypes.value = simpleCopy(props.alertTypes);
  }
);

/**
 * チェックボタンを表示するかどうか
 * @param alertType - アラートの種類
 */
const showCheckButton = (alertType: AlertType) => {
  return alertType === 'OrderAmountWarning';
};

const makeAlertData = computed((): AlertBasicDataObject[] => {
  const res = [] as AlertBasicDataObject[];
  props.alertTypes.map((alertType) => {
    if (!(alertType in OrderAlertKinds)) {
      console.log('==========不正なアラートタイプ==========');
      return res;
    }
    res.push({
      kind: OrderAlertKinds[alertType],
      type: isAlert(alertType) ? 'error' : 'warning',
      title: isAlert(alertType) ? t('警告') : t('注意'),
      description: alertLabel(alertType),
      showConfirmButton: showCheckButton(alertType),
    });
  });
  return res;
});

const isOpening = ref(false);

watch(
  () => props.isOpening,
  () => {
    isOpening.value = props.isOpening;
  }
);
</script>

<template>
  <AlertCell :alert-data="makeAlertData" :prop-is-opening="isOpening" @on-next="emits('onNext', $event)" @click="emits('click', $event)">
    <template v-if="alertTypes.includes('OrderAmountWarning')" #[`alertBody-orderAmountWarning`]>
      <span class="alert-body__content--delivery-date">{{ t('発注アラート：希望納期', { deliveryDate: deliveryDate }) }}</span>
      <span class="alert-body__content--order-amount">{{ t('発注アラート：発注数量', { orderAmount: orderAmount }) }}</span>
    </template>
    <template v-if="alertTypes.includes('DeliveryDateWarning')" #[`alertBody-deliveryDateWarning`]>
      <span class="alert-body__content--delivery-date">{{ t('発注アラート：希望納期', { deliveryDate: deliveryDate }) }}</span>
      <span class="alert-body__content--order-amount">{{ t('発注アラート：発注数量', { orderAmount: orderAmount }) }}</span>
    </template>
  </AlertCell>
</template>
